/**
 * Cross-pack registry for "is it safe to auto-reload right now" and a
 * staleness pub-sub, backed by a `window` singleton. Shakapacker/Rspack
 * compiles multiple independent packs onto one page (e.g. the takeoff-v2
 * pack and a layout-level monitor pack) — a plain module-level variable
 * would give each pack its own, unsynchronized copy of this state.
 */

export type BuildRefreshGuard = {
  id: string;
  isUnsafeToReload: () => boolean;
};

type Registry = {
  guards: Map<string, BuildRefreshGuard>;
  staleListeners: Set<() => void>;
};

declare global {
  interface Window {
    __boonBuildRefreshRegistry?: Registry;
  }
}

function getRegistry(): Registry {
  if (!window.__boonBuildRefreshRegistry) {
    window.__boonBuildRefreshRegistry = { guards: new Map(), staleListeners: new Set() };
  }
  return window.__boonBuildRefreshRegistry;
}

export function registerBuildRefreshGuard(guard: BuildRefreshGuard): () => void {
  const registry = getRegistry();
  registry.guards.set(guard.id, guard);
  return () => {
    // Only remove the exact instance being unregistered — a newer guard
    // may already have superseded it under the same id (e.g. a delayed
    // unmount cleanup firing after a fresh mount re-registered), and that
    // replacement must not be deleted out from under it.
    if (registry.guards.get(guard.id) === guard) {
      registry.guards.delete(guard.id);
    }
  };
}

export function isUnsafeToReload(): boolean {
  for (const guard of getRegistry().guards.values()) {
    if (guard.isUnsafeToReload()) return true;
  }
  return false;
}

/** Called by either detection path when a stale build is found but it's
 * unsafe to reload — drives the shared "update available" banner. */
export function markBuildStale(): void {
  getRegistry().staleListeners.forEach((listener) => listener());
}

export function onBuildStale(listener: () => void): () => void {
  const { staleListeners } = getRegistry();
  staleListeners.add(listener);
  return () => staleListeners.delete(listener);
}

/** Test-only: fully reset the singleton between cases. */
export function __resetBuildRefreshGuardForTests(): void {
  window.__boonBuildRefreshRegistry = { guards: new Map(), staleListeners: new Set() };
}
